import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography, CircularProgress, Divider, Drawer, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { COLOR_CENTER_BORDER } from '../theme/tokens';

import mobiilivarmenneLogo from '../images/bankLogos/mobiilivarmenne.svg';
import nordeaLogo from '../images/bankLogos/nordea.svg';
import opLogo from '../images/bankLogos/op.svg';
import danskeLogo from '../images/bankLogos/danske.svg';
import sPankkiLogo from '../images/bankLogos/s-pankki.svg';
import saastopankkiLogo from '../images/bankLogos/saastopankki.svg';
import omaSaastopankkiLogo from '../images/bankLogos/oma-saastopankki.svg';
import popPankkiLogo from '../images/bankLogos/pop-pankki.svg';
import handelsbankenLogo from '../images/bankLogos/handelsbanken.svg';
import aktiaLogo from '../images/bankLogos/aktia.svg';
import alandsbankenLogo from '../images/bankLogos/alandsbanken.svg';

const useStyles = makeStyles(theme => ({
  root: {
    width: '576px',
    height: '100%',

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },

    '& > *': {
      padding: '32px 24px',
      [theme.breakpoints.down('xs')]: {
        padding: '32px 24px',
      },
    },
  },
  background: {
    background: 'rgba(240, 240, 240, 1)',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  group: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'stretch',
    marginTop: '24px',
  },
  authenticator: {
    textAlign: 'center',
    fontSize: '16px',
    lineHeight: '160%',
    padding: '16px 40px',
    background: '#fff',
    boxSizing: 'border-box',
    width: '50%',
    minWidth: 'auto',
    textTransform: 'uppercase',
    border: '1px solid rgba(235, 235, 234, 1)',

    '&:nth-of-type(2n+1)': {
      borderTop: '0',
    },
    '&:nth-of-type(2n)': {
      borderTop: '0',
      borderLeft: '0',
    },
    '&:nth-of-type(1), &:nth-of-type(2)': {
      borderTop: '1px solid rgba(235, 235, 234, 1)',
    },

    [theme.breakpoints.down('xs')]: {
      width: '100%',
      padding: '16px 20px',
      textAlign: 'left',
      '&:nth-of-type(n)': {
        borderLeft: '1px solid rgba(235, 235, 234, 1)',
        borderTop: '0',
      },
      '&:nth-of-type(1)': {
        borderTop: '1px solid rgba(235, 235, 234, 1)',
      },
    },

  },
  buttonInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
  },
  authenticatorLogoWrapper: {
    height: '64px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      height: '56px',
      width: '120px',
      marginRight: '16px',
      flexShrink: 0,
    },
  },
  authenticatorLogo: {
    height: '64px',
    width: 'auto',
    maxWidth: '134px',
    objectFit: 'contain',
    [theme.breakpoints.down('xs')]: {
      height: 'auto',
      maxHeight: '48px',
      maxWidth: '110px',
    },
  },
  authenticatorName: {
    marginTop: '2px',
    whiteSpace: 'nowrap',
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '0.5px',
    [theme.breakpoints.down('xs')]: {
      marginTop: '0',
      fontSize: '13px',
      fontWeight: 400,
    },
  },
}));

const logos = {
  nordea: nordeaLogo,
  op: opLogo,
  danske: danskeLogo,
  sPankki: sPankkiLogo,
  saastopankki: saastopankkiLogo,
  omaSaastopankki: omaSaastopankkiLogo,
  popPankki: popPankkiLogo,
  handelsbanken: handelsbankenLogo,
  aktia: aktiaLogo,
  alandsbanken: alandsbankenLogo,
  mobiilivarmenne: mobiilivarmenneLogo,
  blank: null,
};

const names = {
  nordea: 'Nordea',
  op: 'OP',
  danske: 'Danske Bank',
  sPankki: 'S-Pankki',
  saastopankki: 'Säästöpankki',
  omaSaastopankki: 'Oma Säästöpankki',
  popPankki: 'POP Pankki',
  handelsbanken: 'Handelsbanken',
  aktia: 'Aktia',
  alandsbanken: 'Ålandsbanken',
  mobiilivarmenne: 'Mobiilivarmenne',
  blank: null,
};

const serviceIds = {
  nordea: '4',
  op: '6',
  danske: '2',
  sPankki: '9',
  saastopankki: '8',
  omaSaastopankki: '5',
  popPankki: '7',
  handelsbanken: '3',
  aktia: '0',
  alandsbanken: '1',
  mobiilivarmenne: '10',
  blank: null,
};

const BankButton = ({ bankKey, onSelect, disabled, loadingServiceId }) => {
  const classes = useStyles();
  const isBlank = !names[bankKey];
  const isLoading = !isBlank && loadingServiceId === serviceIds[bankKey];

  return (
    <Button
      className={classes.authenticator}
      disabled={isBlank || disabled}
      onClick={() => { if (!isBlank) onSelect(serviceIds[bankKey]); }}
    >
      <Box className={classes.buttonInner}>
        <Box className={classes.authenticatorLogoWrapper}>
          {isLoading ? (
            <CircularProgress color='primary' size={32} />
          ) : (
            logos[bankKey] && <img
              src={logos[bankKey]}
              alt={names[bankKey]}
              className={classes.authenticatorLogo}
            />
          )}
        </Box>
        <Typography component='p' className={classes.authenticatorName}>
          {names[bankKey]}
        </Typography>
      </Box>
    </Button>
  );
};

const BankSelectionPanel = ({ open, onClose, onBankSelect, disabled, loadingServiceId }) => {
  const classes = useStyles();

  return (
    <Drawer anchor='right' open={open} onClose={onClose}>
      <Box className={`${classes.root} ${classes.background}`}>
        <Box className={classes.background}>
          <Box className={classes.headerRow}>
            <Typography variant='h4'>
              Tunnistaudu palveluun
            </Typography>
            <IconButton onClick={onClose} edge='end' aria-label='sulje'>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider style={{ margin: '24px 0', backgroundColor: '#BDBDBD', opacity: '0.3' }} />
          <Typography variant='body1'>
            Palvelun käyttäminen on turvallista, joten sen käyttäminen vaatii vahvan tunnistautumisen.
          </Typography>
          <Box className={classes.group}>
            {Object.keys(logos).map(bankKey => (
              <BankButton
                key={bankKey}
                bankKey={bankKey}
                onSelect={onBankSelect}
                disabled={disabled}
                loadingServiceId={loadingServiceId}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BankSelectionPanel;
